import React from 'react'

import {
  Body1,
  Box,
  Color,
  Body2,
  BorderStyle,
  Image,
  Display,
  FlexDirection,
  Overflow,
  Visible,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { ProductButtons } from 'Product/Buttons'
import { useDispatch, useSelector } from 'react-redux'

import { basketProductAdd, basketProductRemove } from 'actions/basket'
import { getProducts } from 'selectors/products'
import { getBasket, getProductCategories } from 'selectors/root'
import { getProductLimitReached } from 'utils/basketProductLimits'

import placeholderSrc from 'media/images/recipe-placeholder.png'

const IMAGE_WIDTH = 1000

export type SidesTileProps = {
  id: string
  images: Record<string, { src: string }>
  listPrice: string
  quantity: number
  stock: number
  title: string
}

const SidesImage = withCitrusTheme(Image, () => ({
  objectFit: 'cover',
  objectPosition: 'center',
}))

export const SidesTile = ({ id, images, listPrice, quantity, stock, title }: SidesTileProps) => {
  const dispatch = useDispatch()
  const basket = useSelector(getBasket)
  const products = useSelector(getProducts)
  const productsCategories = useSelector(getProductCategories)

  const outOfStock = stock <= 0
  const sideImage = images?.[IMAGE_WIDTH]?.src ?? placeholderSrc
  const limitReached = getProductLimitReached(id, basket, products, productsCategories)
  // This is an ugly hack for the chicken breast as it's the only one to be
  // 1 portion where the rest are all 2 (the usual hacky default for all sides)
  const portions = id === 'ce35b738-c98e-11ee-a818-064e823406e3' ? 1 : 2
  const renderProductButtons = () => (
    <ProductButtons
      isAvailable={!outOfStock && !limitReached}
      limitReached={limitReached}
      onAdd={() => dispatch(basketProductAdd(id, 'sides_modal' as any))}
      onRemove={() => dispatch(basketProductRemove(id, 'sides_modal'))}
      outOfStock={outOfStock}
      productId={id}
      qty={quantity}
    />
  )

  return (
    <>
      <Visible display={[Display.Block, Display.None]}>
        <Box
          bg={Color.White}
          borderColor={Color.ColdGrey_100}
          borderStyle={BorderStyle.Solid}
          borderWidth={0.5}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={2}
        >
          <Box height="180px" width="100%">
            <SidesImage alt={title} src={sideImage} width="100%" height="100%" />
          </Box>
          <Box paddingH={3}>
            <Body1 fontWeight={700} color={Color.ColdGrey_800}>
              {title}
            </Body1>
            <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
              <Body2>{`£${listPrice}`}</Body2>
              <Body2>{portions} portions</Body2>
            </Box>
          </Box>
          <Box paddingH={3} paddingBottom={3}>
            {renderProductButtons()}
          </Box>
        </Box>
      </Visible>

      <Visible display={[Display.None, Display.Block]}>
        <Box display={Display.Flex}>
          <Box
            borderRadius={1}
            flexShrink={0}
            height="120px"
            overflowX={Overflow.Hidden}
            overflowY={Overflow.Hidden}
            width="120px"
          >
            <SidesImage alt={title} src={sideImage} width="100%" height="100%" />
          </Box>
          <Box flexGrow={1} paddingH={4} minWidth="0">
            <Body1 fontWeight={700} color={Color.ColdGrey_800}>
              {title}
            </Body1>
            <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
              <Body2>{`£${listPrice}`}</Body2>
              <Body2>{portions} portions</Body2>
            </Box>
          </Box>
          <Box minWidth="130px">{renderProductButtons()}</Box>
        </Box>
      </Visible>
    </>
  )
}
